import { z } from 'zod';
import type { DocumentTemplateCategory } from './document-templates.schemas';

export const DATA_FIELD_KEYS = [
  'OWNERS_NAMES',
  'OWNERS_ADDRESS_BLOCK',
  'OWNER_1_NAME',
  'OWNER_1_BIRTH_DATE',
  'OWNER_1_NATIONALITY',
  'OWNER_1_HOME_ADDRESS',
  'OWNER_1_EMAIL',
  'OWNER_1_PHONE',
  'OWNER_1_BIRTH_NAME',
  'OWNER_1_BIRTH_PLACE',
  'OWNER_2_NAME',
  'OWNER_2_BIRTH_DATE',
  'OWNER_2_NATIONALITY',
  'OWNER_2_HOME_ADDRESS',
  'OWNER_2_EMAIL',
  'OWNER_2_PHONE',
  'OWNER_2_BIRTH_NAME',
  'OWNER_2_BIRTH_PLACE',
  'OWNER_3_NAME',
  'OWNER_3_BIRTH_DATE',
  'OWNER_3_NATIONALITY',
  'OWNER_3_HOME_ADDRESS',
  'OWNER_3_EMAIL',
  'OWNER_3_PHONE',
  'OWNER_3_BIRTH_NAME',
  'OWNER_3_BIRTH_PLACE',
  'OWNER_4_NAME',
  'OWNER_4_BIRTH_DATE',
  'OWNER_4_NATIONALITY',
  'OWNER_4_HOME_ADDRESS',
  'OWNER_4_EMAIL',
  'OWNER_4_PHONE',
  'OWNER_4_BIRTH_NAME',
  'OWNER_4_BIRTH_PLACE',
  'PROPERTY_ADDRESS',
  'PROPERTY_TYPE',
  'DEAL_EXPECTED_SALE_PRICE',
  'MANDATE_TYPE',
  'MANDATE_START_OF_WORK',
  'MANDATE_DURATION',
  'MANDATE_ENDS_ON',
  'MANDATE_SELLER_COMMISSION',
  'MANDATE_BUYER_COMMISSION',
  'LOCATION_NAME',
  'LOCATION_ADDRESS',
  'LOCATION_BROKERS_NAMES',
  'LOCATION_BROKERS_EMAILS',
  'LOCATION_BROKERS_PHONES',
  'LOCATION_BROKERS_REPRESENTATION',
  'COMPANY_NAME',
  'COMPANY_LEGAL_FORM',
  'COMPANY_ADDRESS',
  'COMPANY_MANAGING_DIRECTORS',
  'COMPANY_COMMERCIAL_REGISTER_COURT',
  'COMPANY_COMMERCIAL_REGISTER_NUMBER',
  'COMPANY_VAT_ID',
  'COMPANY_SUPERVISORY_AUTHORITY',
  'COMPANY_PROFESSIONAL_INDEMNITY_INSURANCE',
  'TODAY',
] as const;
export const DataFieldKeySchema = z.enum(DATA_FIELD_KEYS);
export type DataFieldKey = z.infer<typeof DataFieldKeySchema>;

/** Palette groups, in palette order. */
export const DATA_FIELD_GROUPS = [
  'OWNERS',
  'OWNER_1',
  'OWNER_2',
  'OWNER_3',
  'OWNER_4',
  'PROPERTY',
  'DEAL',
  'MANDATE',
  'LOCATION',
  'COMPANY',
  'DATE',
] as const;
export type DataFieldGroup = (typeof DATA_FIELD_GROUPS)[number];

/** The field half of a key, shared by every owner position (labels are keyed by it). */
export const DATA_FIELD_NAMES = [
  'NAMES',
  'ADDRESS_BLOCK',
  'NAME',
  'BIRTH_DATE',
  'NATIONALITY',
  'HOME_ADDRESS',
  'EMAIL',
  'PHONE',
  'BIRTH_NAME',
  'BIRTH_PLACE',
  'ADDRESS',
  'PROPERTY_TYPE',
  'EXPECTED_SALE_PRICE',
  'MANDATE_TYPE',
  'START_OF_WORK',
  'DURATION',
  'ENDS_ON',
  'SELLER_COMMISSION',
  'BUYER_COMMISSION',
  'BROKERS_NAMES',
  'BROKERS_EMAILS',
  'BROKERS_PHONES',
  'BROKERS_REPRESENTATION',
  'COMPANY_NAME',
  'LEGAL_FORM',
  'MANAGING_DIRECTORS',
  'COMMERCIAL_REGISTER_COURT',
  'COMMERCIAL_REGISTER_NUMBER',
  'VAT_ID',
  'SUPERVISORY_AUTHORITY',
  'PROFESSIONAL_INDEMNITY_INSURANCE',
  'TODAY',
] as const;
export type DataFieldName = (typeof DATA_FIELD_NAMES)[number];

type DataFieldParts = { readonly group: DataFieldGroup; readonly field: DataFieldName };

// A table, not prefix-stripping: PROPERTY_TYPE, MANDATE_TYPE and COMPANY_NAME keep
// their prefix in the field name, and TODAY belongs to the DATE group.
const DATA_FIELD_PARTS: { readonly [K in DataFieldKey]: DataFieldParts } = {
  OWNERS_NAMES: { group: 'OWNERS', field: 'NAMES' },
  OWNERS_ADDRESS_BLOCK: { group: 'OWNERS', field: 'ADDRESS_BLOCK' },
  OWNER_1_NAME: { group: 'OWNER_1', field: 'NAME' },
  OWNER_1_BIRTH_DATE: { group: 'OWNER_1', field: 'BIRTH_DATE' },
  OWNER_1_NATIONALITY: { group: 'OWNER_1', field: 'NATIONALITY' },
  OWNER_1_HOME_ADDRESS: { group: 'OWNER_1', field: 'HOME_ADDRESS' },
  OWNER_1_EMAIL: { group: 'OWNER_1', field: 'EMAIL' },
  OWNER_1_PHONE: { group: 'OWNER_1', field: 'PHONE' },
  OWNER_1_BIRTH_NAME: { group: 'OWNER_1', field: 'BIRTH_NAME' },
  OWNER_1_BIRTH_PLACE: { group: 'OWNER_1', field: 'BIRTH_PLACE' },
  OWNER_2_NAME: { group: 'OWNER_2', field: 'NAME' },
  OWNER_2_BIRTH_DATE: { group: 'OWNER_2', field: 'BIRTH_DATE' },
  OWNER_2_NATIONALITY: { group: 'OWNER_2', field: 'NATIONALITY' },
  OWNER_2_HOME_ADDRESS: { group: 'OWNER_2', field: 'HOME_ADDRESS' },
  OWNER_2_EMAIL: { group: 'OWNER_2', field: 'EMAIL' },
  OWNER_2_PHONE: { group: 'OWNER_2', field: 'PHONE' },
  OWNER_2_BIRTH_NAME: { group: 'OWNER_2', field: 'BIRTH_NAME' },
  OWNER_2_BIRTH_PLACE: { group: 'OWNER_2', field: 'BIRTH_PLACE' },
  OWNER_3_NAME: { group: 'OWNER_3', field: 'NAME' },
  OWNER_3_BIRTH_DATE: { group: 'OWNER_3', field: 'BIRTH_DATE' },
  OWNER_3_NATIONALITY: { group: 'OWNER_3', field: 'NATIONALITY' },
  OWNER_3_HOME_ADDRESS: { group: 'OWNER_3', field: 'HOME_ADDRESS' },
  OWNER_3_EMAIL: { group: 'OWNER_3', field: 'EMAIL' },
  OWNER_3_PHONE: { group: 'OWNER_3', field: 'PHONE' },
  OWNER_3_BIRTH_NAME: { group: 'OWNER_3', field: 'BIRTH_NAME' },
  OWNER_3_BIRTH_PLACE: { group: 'OWNER_3', field: 'BIRTH_PLACE' },
  OWNER_4_NAME: { group: 'OWNER_4', field: 'NAME' },
  OWNER_4_BIRTH_DATE: { group: 'OWNER_4', field: 'BIRTH_DATE' },
  OWNER_4_NATIONALITY: { group: 'OWNER_4', field: 'NATIONALITY' },
  OWNER_4_HOME_ADDRESS: { group: 'OWNER_4', field: 'HOME_ADDRESS' },
  OWNER_4_EMAIL: { group: 'OWNER_4', field: 'EMAIL' },
  OWNER_4_PHONE: { group: 'OWNER_4', field: 'PHONE' },
  OWNER_4_BIRTH_NAME: { group: 'OWNER_4', field: 'BIRTH_NAME' },
  OWNER_4_BIRTH_PLACE: { group: 'OWNER_4', field: 'BIRTH_PLACE' },
  PROPERTY_ADDRESS: { group: 'PROPERTY', field: 'ADDRESS' },
  PROPERTY_TYPE: { group: 'PROPERTY', field: 'PROPERTY_TYPE' },
  DEAL_EXPECTED_SALE_PRICE: { group: 'DEAL', field: 'EXPECTED_SALE_PRICE' },
  MANDATE_TYPE: { group: 'MANDATE', field: 'MANDATE_TYPE' },
  MANDATE_START_OF_WORK: { group: 'MANDATE', field: 'START_OF_WORK' },
  MANDATE_DURATION: { group: 'MANDATE', field: 'DURATION' },
  MANDATE_ENDS_ON: { group: 'MANDATE', field: 'ENDS_ON' },
  MANDATE_SELLER_COMMISSION: { group: 'MANDATE', field: 'SELLER_COMMISSION' },
  MANDATE_BUYER_COMMISSION: { group: 'MANDATE', field: 'BUYER_COMMISSION' },
  LOCATION_NAME: { group: 'LOCATION', field: 'NAME' },
  LOCATION_ADDRESS: { group: 'LOCATION', field: 'ADDRESS' },
  LOCATION_BROKERS_NAMES: { group: 'LOCATION', field: 'BROKERS_NAMES' },
  LOCATION_BROKERS_EMAILS: { group: 'LOCATION', field: 'BROKERS_EMAILS' },
  LOCATION_BROKERS_PHONES: { group: 'LOCATION', field: 'BROKERS_PHONES' },
  LOCATION_BROKERS_REPRESENTATION: { group: 'LOCATION', field: 'BROKERS_REPRESENTATION' },
  COMPANY_NAME: { group: 'COMPANY', field: 'COMPANY_NAME' },
  COMPANY_LEGAL_FORM: { group: 'COMPANY', field: 'LEGAL_FORM' },
  COMPANY_ADDRESS: { group: 'COMPANY', field: 'ADDRESS' },
  COMPANY_MANAGING_DIRECTORS: { group: 'COMPANY', field: 'MANAGING_DIRECTORS' },
  COMPANY_COMMERCIAL_REGISTER_COURT: { group: 'COMPANY', field: 'COMMERCIAL_REGISTER_COURT' },
  COMPANY_COMMERCIAL_REGISTER_NUMBER: { group: 'COMPANY', field: 'COMMERCIAL_REGISTER_NUMBER' },
  COMPANY_VAT_ID: { group: 'COMPANY', field: 'VAT_ID' },
  COMPANY_SUPERVISORY_AUTHORITY: { group: 'COMPANY', field: 'SUPERVISORY_AUTHORITY' },
  COMPANY_PROFESSIONAL_INDEMNITY_INSURANCE: { group: 'COMPANY', field: 'PROFESSIONAL_INDEMNITY_INSURANCE' },
  TODAY: { group: 'DATE', field: 'TODAY' },
};

/** `OWNER_2_BIRTH_DATE` → `{ group: 'OWNER_2', field: 'BIRTH_DATE' }`; `TODAY` → `{ group: 'DATE', field: 'TODAY' }`. */
export function dataFieldParts(key: DataFieldKey): DataFieldParts {
  return DATA_FIELD_PARTS[key];
}

/** `MANDATE` and `LOCATION` exist for `BROKER_CONTRACT` only (PRD `## Fields`); every other group for both. */
export function isDataFieldAllowedIn(key: DataFieldKey, category: DocumentTemplateCategory): boolean {
  const { group } = dataFieldParts(key);

  return (group !== 'MANDATE' && group !== 'LOCATION') || category === 'BROKER_CONTRACT';
}
